import { StatsigUser } from '@statsig/client-core';
import { User } from '@core/redux/stores/user.store';
import {
  CIAM_NUDGE_BUDGET_EXHAUSTED_KEY,
  CIAM_NUDGE_DISMISSAL_COUNT_KEY,
  CIAM_NUDGE_LAST_DISMISSED_KEY
} from '@core/constants/storage-keys';
import data from '../../../project.json';

const FINGERPRINT_KEY = 'browserFingerPrint';

interface StatsigCustomFields {
  [key: string]: string | number | boolean | undefined;
  devicePlatform: 'Web';
  appVersion: string;
  client: 'Web';
  anonymous: boolean;
  deviceId?: string;
  accountType?: string;
  loyaltyCustomerId?: string;
  ciamNudgeDismissalCount?: number;
  ciamNudgeLastDismissedAt?: string;
  ciamNudgeBudgetExhausted?: boolean;
}

function parseAppVersion(raw: unknown): string {
  const version = typeof raw === 'string' ? raw : '';
  return version.match(/^\d+\.\d+\.\d+/)?.[0] ?? version;
}

function getBrowserFingerprint(): string | undefined {
  try {
    const fingerprint = localStorage.getItem(FINGERPRINT_KEY);
    return fingerprint && fingerprint.trim() !== '' ? fingerprint : undefined;
  } catch {
    return undefined;
  }
}

function hasValidUserId(user: User | null | undefined): user is User & { id: string } {
  return typeof user?.id === 'string' && user.id.trim() !== '';
}

/**
 * Stamps the current migration nudge ladder onto the user so it appears in the Log Stream's User
 * Properties column on every event, rather than having to be reconstructed from event history.
 * Storage is read directly: depending on MigrationNudgeStateService here would close a cycle back
 * through FeatureFlagService. Observation only, never rule targeting.
 */
function addMigrationNudgeState(custom: StatsigCustomFields): void {
  try {
    const count = parseInt(localStorage.getItem(CIAM_NUDGE_DISMISSAL_COUNT_KEY) ?? '', 10);
    custom.ciamNudgeDismissalCount = Number.isFinite(count) && count > 0 ? count : 0;
    custom.ciamNudgeBudgetExhausted = localStorage.getItem(CIAM_NUDGE_BUDGET_EXHAUSTED_KEY) === 'true';

    const lastDismissedAt = parseInt(localStorage.getItem(CIAM_NUDGE_LAST_DISMISSED_KEY) ?? '', 10);
    if (Number.isFinite(lastDismissedAt) && lastDismissedAt > 0) {
      custom.ciamNudgeLastDismissedAt = new Date(lastDismissedAt).toISOString();
    }
  } catch {
    //a user built without these properties is still a usable user
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

export function buildStatsigUser(user: User | null | undefined): StatsigUser {
  const fingerprint = getBrowserFingerprint();
  const appVersion = parseAppVersion(data.version);
  const isAnonymous = !hasValidUserId(user);

  const custom: StatsigCustomFields = {
    devicePlatform: 'Web',
    appVersion,
    client: 'Web',
    anonymous: isAnonymous,
    ...(fingerprint ? { deviceId: fingerprint } : {})
  };

  let userID: string | undefined;

  if (hasValidUserId(user)) {
    userID = user.id;
    if (isNonEmptyString(user.mainAccountType)) {
      custom.accountType = user.mainAccountType;
    }
    if (isNonEmptyString(user.beanstalkContactId)) {
      custom.loyaltyCustomerId = user.beanstalkContactId;
    }
  }

  addMigrationNudgeState(custom);

  return {
    ...(userID ? { userID } : {}),
    ...(fingerprint ? { customIDs: { stableID: fingerprint } } : {}),
    custom
  };
}
